// Feature: features/nma-claims-known-issues-tabs.md, features/nma-claims-beta-banner-and-blurred-summaries.md, features/nma-claims-performance-and-component-extraction.md
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Tooltip as ChakraTooltip } from '@chakra-ui/react';

export const normalizeAnalysisText = (rawText) => {
  if (!rawText) return '';
  return String(rawText)
    .replace(/\$?\\rightarrow\$?/g, '->')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\u00A0\u00A0\u00A0\u00A0')
    .replace(/\t/g, '\u00A0\u00A0\u00A0\u00A0');
};

export const FormattedAnalysisText = ({ text, fontSize = '11px', color = 'gray.700', mt = 0.5, mb = 0 }) => {
  const normalized = normalizeAnalysisText(text);
  if (!normalized) return null;
  return (
    <Box
      mt={mt}
      mb={mb}
      fontSize={fontSize}
      color={color}
      lineHeight="1.5"
      sx={{
        '& p': { mb: 2, whiteSpace: 'pre-wrap', '&:last-child': { mb: 0 } },
        '& ul': { listStyleType: 'disc', pl: 5, mb: 2, '&:last-child': { mb: 0 } },
        '& ol': { listStyleType: 'decimal', pl: 5, mb: 2, '&:last-child': { mb: 0 } },
        '& li': { mb: 2, whiteSpace: 'pre-wrap', '&:last-child': { mb: 0 } },
        '& h1, & h2, & h3, & h4': { fontWeight: 'bold', mt: 1.5, mb: 1 },
        '& strong, & b': { fontWeight: 'bold' },
        '& code': {
          bg: 'blackAlpha.100',
          px: 1,
          py: 0.5,
          borderRadius: 'sm',
          fontSize: '0.92em',
          fontFamily: 'mono',
        },
        '& pre': {
          bg: 'blackAlpha.50',
          p: 2,
          borderRadius: 'sm',
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          mb: 1.5,
        },
      }}
    >
      <ReactMarkdown>{normalized}</ReactMarkdown>
    </Box>
  );
};

export const formatCurrency = (val) => {
  const num = Number(val || 0);
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const BETA_SUMMARY_TOOLTIP =
  'Summary financial figures are blurred during Beta validation (numbers may not be accurate).';

// Blurred summary dollar helper per NMA beta requirements (see features/nma-claims-beta-banner-and-blurred-summaries.md)
export const BlurredCurrency = ({ placeholder = '$88,888.88' }) => (
  <ChakraTooltip label={BETA_SUMMARY_TOOLTIP} hasArrow>
    <Box
      as="span"
      display="inline-block"
      sx={{ filter: 'blur(5px)', userSelect: 'none' }}
      cursor="not-allowed"
      aria-label="Blurred beta summary amount"
    >
      {placeholder}
    </Box>
  </ChakraTooltip>
);

export const formatLabel = (raw) => {
  if (!raw) return 'N/A';
  return String(raw).replace(/_/g, ' ');
};

export const getStatusColorScheme = (status) => {
  if (!status) return 'gray';
  const s = status.toUpperCase();
  if (s === 'PAID') return 'green';
  if (s === 'ALLOWED_APPLIED_TO_DEDUCTIBLE') return 'teal';
  if (s.includes('PARTIALLY_PAID')) return 'orange';
  if (s.includes('PENDED') || s.includes('RECORDS_REQUESTED')) return 'purple';
  if (s.includes('RECORDS_SENT') || s.includes('APPEAL_SUBMITTED')) return 'blue';
  if (s.includes('DENIED') || s.includes('REJECTED')) return 'red';
  return 'gray';
};

export const getUrgencyColorScheme = (urgency) => {
  const u = String(urgency || '').toUpperCase();
  if (u === 'HIGH') return 'red';
  if (u === 'MEDIUM') return 'orange';
  return 'green';
};

export const getCategoryColorScheme = (cat) => {
  const c = String(cat || '').toUpperCase();
  if (c === 'PAID') return 'green';
  if (c === 'ALLOWED_APPLIED_TO_DEDUCTIBLE') return 'teal';
  if (c === 'RECORDS_REQUESTED') return 'purple';
  if (c === 'PRIOR_AUTHORIZATION') return 'pink';
  if (c === 'MEDICAL_NECESSITY') return 'red';
  if (c === 'CODING_OR_BILLING_ERROR') return 'orange';
  if (c === 'BUNDLING_NCCI') return 'yellow';
  return 'gray';
};
